// Base Project Types
export interface DashboardProject {
  projectName: string;
  projectNumber: string;
  location: string;
  customer: string;
  startDate: string;
  endDate: string;
  projectTypes: {
    pipeline: boolean;
    fabrication: boolean;
    others: boolean;
  };
  fabricationOptions?: {
    buckleArrestor: boolean;
    pipeInPipe: boolean;
    bulkhead: boolean;
  };
  features: {
    welding: boolean;
    coating: boolean;
    assembly: boolean;
    testing: boolean;
    inspection: boolean;
  };
  projectScope: {
    engineering: boolean;
    procurement: boolean;
    construction: boolean;
    commissioning: boolean;
  };
}

// Project Setup Types
export interface PipeSize {
  size: string;
  wallThickness: string;
  buckleArrestors: string;
  numberOfPipes: string;
  pipeLength: string;
  numberOfForges: string;
  outputAssemblyLength: string;
  manufacturer: string;
}

// Base Material Types
interface BaseMaterial {
  id: string;
  status: 'Available' | 'Consumed';
  documents?: MaterialDocument[];
}

// Pipeline Project Material Types
export interface PipelineMaterial extends BaseMaterial {
  category: 'Pipe' | 'Forge' | 'Gas' | 'Wire Lot';
  pipeId?: string;
  // Pipe specific fields
  pipeNRI?: string;
  pipeNo?: string;
  heatNumber?: string;
  length?: number;
  outerDiameter?: number;
  wallThickness?: number;
  assemblyNumber?: string;
  // Forge specific fields
  forgeNRI?: string;
  forgeOrderNumber?: string;
  forgeSerialNumber?: string;
  forgeHeatNumber?: string;
  // Wire Lot specific fields
  lotNumber?: string;
  // Gas specific fields
  gasSerialNumber?: string;
  // Multiple assembly numbers for Gas and Wire Lot
  assemblyNumbers?: string[];
}

// Fabrication Project Material Types
export interface FabricationMaterial extends BaseMaterial {
  category: 'Buckle Arrestor' | 'Pipe In Pipe' | 'Bulkhead' | 'Raw Material';
  // Buckle Arrestor specific fields
  arrestorType?: string;
  arrestorSize?: string;
  arrestorLength?: number;
  arrestorWeight?: number;
  // Pipe In Pipe specific fields
  outerPipeSize?: string;
  innerPipeSize?: string;
  insulationType?: string;
  totalLength?: number;
  // Bulkhead specific fields
  bulkheadType?: string;
  bulkheadDiameter?: number;
  bulkheadThickness?: number;
  // Raw Material specific fields
  materialType?: string;
  materialGrade?: string;
  quantity?: number;
  unit?: string;
}

// Combined Material Type
export type Material = PipelineMaterial | FabricationMaterial;

// Type guard functions
export const isPipelineMaterial = (material: Material): material is PipelineMaterial => {
  return material.category === 'Pipe' || material.category === 'Forge' || 
         material.category === 'Gas' || material.category === 'Wire Lot';
};

export const isFabricationMaterial = (material: Material): material is FabricationMaterial => {
  return material.category === 'Buckle Arrestor' || material.category === 'Pipe In Pipe' || 
         material.category === 'Bulkhead' || material.category === 'Raw Material';
};

export interface MaterialDocument {
  id: string;
  file: File;
  tags: string[];
}

export interface DocumentTag {
  tags_name: string;
  tags_id: number;
  tags_type?: string;
  /** Present on project-scoped tags from `/tags/createTag` / `getAllTags`. */
  project_id?: number | string;
}

/** `tags_type` for fit-up bevel method options (project-scoped). */
export const WELD_FITUP_BEVEL_METHOD_TAGS = 'WELD_FITUP_BEVEL_METHOD_TAGS';

/** `tags_type` for preheat measurement method options (project-scoped). */
export const WELD_PREHEAT_MEASUREMENT_METHOD_TAGS = 'WELD_PREHEAT_MEASUREMENT_METHOD_TAGS';

/** `tags_type` for welding panel weld type options (project-scoped). */
export const WELD_WELD_TYPE_TAGS = 'WELD_WELD_TYPE_TAGS';
export interface DocumentTag1 {
  id: string;
  name: string;
  
}
// Team Management Types
export interface TeamMember {
  id: string;
  name: string;
  role: string;
  email: string;
  phone: string;
  certifications: string[];
  qualifications: string[];
  documents: TeamMemberDocument[];
  status: 'active' | 'inactive';
  // API mapping fields
  team_member_id?: number;
  user_id?: number;
  project_id?: number;
  project_level_role_id?: number;
  apiData?: {
    team_member_id: number;
    user_id: number;
    project_id: number;
    project_level_role_id: number;
    status: string;
    user: {
      user_id: number;
      first_name: string;
      last_name: string;
      email: string;
      phone_number?: string;
      profile_picture_url?: string;
      status?: string;
      department?: {
        department_id: number;
        department_name: string;
        is_active: boolean;
      };
      company?: {
        company_id: number;
        company_name: string;
        is_active: boolean;
      };
      appLevelRole?: {
        app_level_role_id: number;
        group_name: string;
        permissions: Record<string, any>;
        is_active: boolean;
      };
      cognitoStatus?: {
        username: string;
        status: string;
        enabled: boolean;
        userCreateDate: string;
        userLastModifiedDate: string;
      };
    };
    project: {
      project_id: number;
      project_name: string;
      project_number: string;
    };
    projectLevelRole: {
      project_level_role_id: number;
      project_role_name: string;
      permissions: {
        team: string[];
        tasks: string[];
        project: string[];
        reports: string[];
        documents: string[];
      };
    };
    certificates: Array<{
      cert_id: number;
      user_id: number;
      team_member_id: number;
      file_name: string;
      docRef: number;
      file_url: string;
      s3Key: string;
      document_tags: string[] | null;
      created_at: string;
      updated_at: string;
    }>;
  };
}

export interface TeamMemberDocument {
  id: string;
  fileName: string;
  type: 'certification' | 'training' | 'qualification' | 'other';
  uploadDate: string;
  file_url?: string;
  s3Key?: string;
  document_tags?: string[];
  // Additional fields for certificates
  cert_id?: number;
  user_id?: number;
  team_member_id?: number;
}

// Welding Management Types
export interface WeldPass {
  name: string;
  status: 'Completed' | 'Not Started' | 'In Progress' | 'Rejected';
  duration: string;
  welderId:string;
  voltage?: { min: number; avg: number; max: number };
  current?: { min: number; avg: number; max: number };
  travelSpeed?: { min: number; avg: number; max: number };
  heat?: { min: number; avg: number; max: number };
  trueEnergy?: { min: number; avg: number; max: number };
  oscillationWidth?: { min: number; avg: number; max: number };
}

export interface WeldData {
  weldNumber: 'Weld 1' | 'Weld 2';
  weldIdentifier: string;
  status: 'Not Started' | 'In Progress' | 'Completed' | 'Rejected';
  weldTime: string;
  passes?: WeldPass[];
}

export interface AssemblyWeldData {
  assemblyNumber: string;
  welds: WeldData[];
}

// Document Management Types
export interface SpecificationDocument {
  id: string;
  fileName?: string;
  type: 'WPS' | 'DRAWING' | 'WELD_PROCESS' | 'INSPECTION_PROCEDURE' | 'ACCEPTANCE_CRITERIA'| 'ALIGNMENT' | 'PASS_CONFIG' | 'FITMENT_READINGS' | 'WELDING_POSITION' | 'BEVEL' | 'FAT' | 'COATING' | 'OTHERS';
  pipeId: any;
  docRefId?: number;
  projectId?: number;
  projectTypeId?: number;
  projectType?: string;
  projectSubType?: string;
  fabricationSubType?: string;
  pipeOrder?: number;
  s3Key?: string;
  files?:any;
  fileUrl?: string;
  uploadedBy?: string;
  uploadDate: string;
  version?: number;
  details?: any;
  productionData?: any;
  materialsData?: any;
  documentsData?: any;
  // details?: {
  //   weldProcess?: {
  //     processType: string;
  //     wireType: string;
  //     wireSize: string;
  //     shieldingGas: string;
  //     flowRate: string;
  //     voltage: string;
  //     amperage: string;
  //   };
  //   inspectionProcedure?: {
  //     method: string;
  //     equipment: string;
  //     frequency: string;
  //     coverage: string;
  //     calibrationRequirements: string;
  //     standardName: string;
  //     document?: File;
  //   };
  //   acceptanceCriteria?: {
  //     standard: string;
  //     defectTypes: string[];
  //     tolerances: string;
  //     repairProcedure: string;
  //     document?: File;
  //   };
  //   passConfig?: {
  //     numberOfPasses: number;
  //     passes: Array<{
  //       name: string;
  //       sequence: number;
  //       requirements: string;
  //     }>;
  //   };
  //   fitmentReadings?: {
  //     gapTolerance: string;
  //     hiLowTolerance: string;
  //     ovality: string;
  //     straightness: string;
  //     measurementPoints: number;
  //     numberOfReadings: number;
  //     landMin: string;
  //     landMax: string;
  //     wallThicknessMin: string;
  //     wallThicknessMax: string;
  //   };
  //   weldingPosition?: {
  //     position: string;
  //     rotationAllowed: boolean;
  //     maxRotationAngle: string;
  //     fixedPosition: string;
  //     restrictions: string[];
  //   };
  //   bevel?: {
  //     type: string;
  //     angle: string;
  //     rootFace: string;
  //     rootGap: string;
  //     landThickness: string;
  //     preparation: string;
  //   };
  // };
}

export interface NDTData {
  id: string;
  assemblyNumber: string;
  file: File;
  uploadDate: string;
  status: 'pending' | 'approved' | 'rejected';
} 